import {call, delay, put, select, spawn, takeEvery, takeLatest} from 'redux-saga/effects';
import {PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api/api";
import {RootState} from "../../store/rootReducer";
import {QuickAddItemDto} from "../../common";
import {quickAddActions} from "./quickAdd.slice";

const POLL_INTERVAL_MS = 2000;

const selectItems = (state: RootState) => state.quickAdd.items;

function* loadQueueSaga() {
    try {
        const items: QuickAddItemDto[] = yield call(api.quickAdd.list);
        yield put(quickAddActions.setItems(items));
        yield put(quickAddActions.startProcessing());
    } catch {
        // Ignore load errors — the queue simply stays as-is.
    }
}

function* submitSaga() {
    const text: string = yield select((state: RootState) => state.quickAdd.inputText.trim());
    if (text === '') return;

    yield put(quickAddActions.setInputText(''));

    try {
        const item: QuickAddItemDto = yield call(api.quickAdd.create, text);
        yield put(quickAddActions.upsertItem(item));
        yield put(quickAddActions.startProcessing());
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to add to queue');
    }
}

function* startItemSaga(item: QuickAddItemDto) {
    try {
        const updated: QuickAddItemDto = yield call(api.quickAdd.process, item.id);
        yield put(quickAddActions.upsertItem(updated));
    } catch (error) {
        yield put(quickAddActions.upsertItem({
            ...item,
            status: 'error',
            error: error instanceof Error ? error.message : 'Request failed',
        }));
    }
}

function* refreshItemsSaga() {
    try {
        const items: QuickAddItemDto[] = yield call(api.quickAdd.list);
        yield put(quickAddActions.mergeItems(items));
    } catch {
        return;
    }
}

function* processQueueSaga() {
    yield put(quickAddActions.setProcessing(true));
    try {
        while (true) {
            const items: QuickAddItemDto[] = yield select(selectItems);
            const pending = items.filter(item => item.status === 'pending');

            for (const item of pending) {
                yield put(quickAddActions.upsertItem({...item, status: 'processing'}));
                yield spawn(startItemSaga, item);
            }

            const hasWork = pending.length > 0 || items.some(item => item.status === 'processing');
            if (!hasWork) break;

            yield delay(POLL_INTERVAL_MS);
            yield call(refreshItemsSaga);
        }
    } finally {
        yield put(quickAddActions.setProcessing(false));
    }
}

function* deleteItemSaga(action: PayloadAction<string>) {
    yield call(api.quickAdd.remove, action.payload);
    yield put(quickAddActions.removeItemFromStore(action.payload));
}

function* deleteGeneratedCardSaga(action: PayloadAction<{itemId: string; direction: 'normal' | 'inverted'; cardId: string}>) {
    try {
        yield call(api.deleteCard, {cardId: action.payload.cardId});
        yield put(quickAddActions.clearGeneratedCard({itemId: action.payload.itemId, direction: action.payload.direction}));
    } catch (error) {
        window.alert(error instanceof Error ? error.message : 'Failed to delete card');
    }
}

function* retryItemSaga(action: PayloadAction<string>) {
    try {
        const updated: QuickAddItemDto = yield call(api.quickAdd.process, action.payload);
        yield put(quickAddActions.upsertItem(updated));
        yield put(quickAddActions.startProcessing());
    } catch {
        // Leave the item in its error state; the user can retry again.
    }
}

function* clearFinishedSaga() {
    yield call(api.quickAdd.clearFinished);
    yield put(quickAddActions.removeFinishedFromStore());
}

export function* quickAddSaga() {
    yield takeEvery(quickAddActions.loadQueue.type, loadQueueSaga);
    yield takeEvery(quickAddActions.submit.type, submitSaga);
    yield takeLatest(quickAddActions.startProcessing.type, processQueueSaga);
    yield takeEvery(quickAddActions.deleteItem.type, deleteItemSaga);
    yield takeEvery(quickAddActions.deleteGeneratedCard.type, deleteGeneratedCardSaga);
    yield takeEvery(quickAddActions.retryItem.type, retryItemSaga);
    yield takeEvery(quickAddActions.clearFinished.type, clearFinishedSaga);
}
